import React from 'react';

import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';

import { useQueryClient } from '@lumapps/base-api/react-query';
import { InstanceListMetadataRequest } from '@lumapps/metadata/api/instanceMetadataApi';
import { useQueryMetadataByIds } from '@lumapps/metadata/hooks/useQueryMetadataByIds';
import { useQueryMetadataList } from '@lumapps/metadata/hooks/useQueryMetadataList';
import { Metadata } from '@lumapps/metadata/types';
import type { Fields } from '@lumapps/utils/types/Fields';

type MetadataParams = InstanceListMetadataRequest;

export interface UseMetadataListWithSelectedOptions {
    /** metadata query params */
    params: MetadataParams;
    /** list of already selected metadatas */
    selectedMetadatas?: string[];
    /** whether the metadatas should be fetched when this hook is mounted */
    fetchOnMount?: boolean;
    /** Retrieve metadata restricted to one of those content types, empty by default, all unrestricted metadata are returned */
    contentTypes?: string[];
    /** Limit the fields returned by the API to reduce payload size. When omitted, all fields are returned. */
    fields?: Fields<Metadata>;
}

export interface UseMetadataListWithSelected {
    /** parent metadata grouped by their id */
    parentMetadataGroupedById: Record<string, Metadata>;
    /** selected metadatas that correspond to the `selectedMetadatas` ids */
    selectedMetadatas: Record<string, Metadata | Metadata[]>;
    /** list of parent metadatas */
    parentMetadatas: Metadata[];
    /** list of fetched metadatas */
    metadatas: Metadata[];
    /** whether the metadatas are still loading or not */
    isLoading: boolean;
    /** whether there was an error while loading metadatas */
    hasError: boolean;
    /** whether metadatas have loaded or not */
    hasLoaded: boolean;
    /** callback for resetting the loaded data */
    reset: () => void;
    /** callback for resetting the selected metadata  */
    resetSelectedMetadata: () => void;
}

/**
 * Custom metadata hook that retrieves:
 * - A list of parent metadata from a given endpoint
 * - A list of selected metadata given their ids.
 * The use case, displaying a list of metadata that have values
 * @param UseMetadataListWithSelectedOptions
 * @returns UseMetadataListWithSelected
 */
export const useMetadataListWithSelected = ({
    params,
    selectedMetadatas = [],
    fetchOnMount = true,
    contentTypes,
    fields,
}: UseMetadataListWithSelectedOptions): UseMetadataListWithSelected => {
    const queryClient = useQueryClient();
    const areThereaAnySelectedMetadatas = selectedMetadatas.length > 0;

    // Omit `fields` — InstanceListMetadataRequest.fields is `string` (raw API query string)
    // but useQueryMetadataList expects `fields?: Fields<Metadata>` (array). Callers never set it.
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { fields: _fields, ...paramsWithoutFields } = params;
    const listQuery = useQueryMetadataList({ source: 'instance', ...paramsWithoutFields, fields }, fetchOnMount);

    const selectedQuery = useQueryMetadataByIds(fetchOnMount && areThereaAnySelectedMetadatas ? selectedMetadatas : []);

    const fetchedMetadataList: Metadata[] = listQuery.data ?? [];

    /**
     * Filter's out all metadata that are restricted for a specific content type, if that's content type is not required
     * */
    const items = contentTypes
        ? fetchedMetadataList.filter(
              (metadata) =>
                  isEmpty(metadata.customContentTypes) ||
                  metadata.customContentTypes.some((cct) => contentTypes?.includes(cct)),
          )
        : fetchedMetadataList;

    const parentMetadatas = items.filter((m) => (params.rootOnly ? !m.instance : !m.parent));
    const parentMetadataGroupedById = keyBy(parentMetadatas, 'id');

    const isLoading = listQuery.isFetching || (areThereaAnySelectedMetadatas ? selectedQuery.isLoading : false);
    const hasError = listQuery.isError || (areThereaAnySelectedMetadatas ? selectedQuery.hasError : false);
    const hasLoaded =
        !isLoading && !hasError && listQuery.isSuccess && (!areThereaAnySelectedMetadatas || !selectedQuery.isLoading);

    const reset = React.useCallback(() => {
        queryClient.removeQueries({ queryKey: ['metadata', 'list'] });
    }, [queryClient]);

    const resetSelectedMetadata = React.useCallback(() => {
        queryClient.removeQueries({ queryKey: ['metadata', 'get'] });
    }, [queryClient]);

    return {
        parentMetadataGroupedById,
        parentMetadatas,
        metadatas: fetchedMetadataList,
        selectedMetadatas: groupBy(
            (selectedQuery.items ?? []).filter((m): m is Metadata => Boolean(m)),
            'familyKey',
        ),
        isLoading,
        hasError,
        hasLoaded,
        reset,
        resetSelectedMetadata,
    };
};
